'use client';

import { usePathname } from 'next/navigation';
import {
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useShallow } from 'zustand/react/shallow';
import { IconComponent } from '@/components/IconComponent';
import { useGetCommonStore } from '@/store';
import { MenuClientHideConditions, type ServerLink } from '../helpers';
import { MenuLinkClientWrapper } from './MenuLinkClientWrapper';
import { MenuMore } from './MenuMore';
import { ScMenu, ScStripe, type ScStripeProps } from './styled';

export interface MenuProps {
  locale: string;
  links: ServerLink[];
  moreLinks?: ServerLink[];
  withStripe?: boolean;
  signOutBtn?: React.ReactElement | null;
}

export const getIsActiveLink = (
  url: string,
  currentPage: string,
  activeItemRef: RefObject<HTMLLIElement>
) =>
  url === currentPage || (url === '/' && currentPage.slice(0, 5) === '/game')
    ? activeItemRef
    : null;

export const Menu = ({
  links,
  moreLinks,
  withStripe,
  signOutBtn,
}: MenuProps) => {
  const currentPage = usePathname();
  const useCommonStore = useGetCommonStore();

  const { isMobileDevice, isAuthenticated } = useCommonStore(
    useShallow((state) => ({
      isMobileDevice: state.isMobileDevice,
      isAuthenticated: state.isAuthenticated,
    }))
  );

  const [stripe, setStripe] = useState<ScStripeProps | undefined>(undefined);

  const activeItemRef = useRef<HTMLLIElement>(null);

  const setPositionStripe = useCallback(() => {
    if (
      withStripe == null ||
      activeItemRef.current == null ||
      currentPage == null
    ) {
      setStripe(undefined);
      return;
    }

    const element = activeItemRef.current;
    const stripeWidth = element.clientWidth;
    const stripeLeft = element.offsetLeft + window.scrollX;

    setStripe({ stripeWidth, stripeLeft });
  }, [withStripe, setStripe, currentPage, activeItemRef]);

  useEffect(() => {
    setPositionStripe();
  }, [isAuthenticated, setPositionStripe, activeItemRef.current?.offsetWidth]);

  useEffect(() => {
    setPositionStripe();

    window.addEventListener('resize', setPositionStripe);
    return () => window.removeEventListener('resize', setPositionStripe);
  }, [setPositionStripe]);

  const arrowElement = useMemo(() => {
    if (!isMobileDevice) return null;
    return (
      <div className="arrow">
        <IconComponent name="whiteArrowRight" />
      </div>
    );
  }, [isMobileDevice]);

  const getLoyaltyData = useCommonStore.use.getLoyaltyData();
  const isLoyaltyDataExists = getLoyaltyData();

  const hideConditionResults: Record<string, boolean> = {
    [MenuClientHideConditions.NotAuthenticated]: !isAuthenticated,
    [MenuClientHideConditions.NoLoyaltyData]: !isLoyaltyDataExists,
  };

  const visibleMoreLinks = (moreLinks || []).filter(
    ({ hideConditions }) =>
      !hideConditions?.some((condition) => hideConditionResults[condition])
  );

  return (
    <ScMenu>
      {links.map(({ id, url, hideConditions, serverComponent }) => (
        <MenuLinkClientWrapper
          hideConditionResults={hideConditionResults}
          hideConditions={hideConditions}
          id={id}
          key={id}
          ref={getIsActiveLink(url, currentPage, activeItemRef)}
          url={url}
        >
          {serverComponent}
        </MenuLinkClientWrapper>
      ))}
      {moreLinks?.map(({ id, url, hideConditions, serverComponent }) => (
        <MenuLinkClientWrapper
          className="menu-wide-only"
          hideConditionResults={hideConditionResults}
          hideConditions={hideConditions}
          id={id}
          key={id}
          ref={getIsActiveLink(url, currentPage, activeItemRef)}
          url={url}
        >
          {serverComponent}
        </MenuLinkClientWrapper>
      ))}

      {!!visibleMoreLinks.length && (
        <MenuMore
          hideConditionResults={hideConditionResults}
          links={visibleMoreLinks}
        />
      )}

      {signOutBtn && (
        <li>
          {signOutBtn}
          {arrowElement}
        </li>
      )}
      {withStripe && stripe && (
        <ScStripe
          stripeLeft={stripe.stripeLeft}
          stripeWidth={stripe.stripeWidth}
        />
      )}
    </ScMenu>
  );
};
